import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { FiSearch, FiUserPlus, FiX } from 'react-icons/fi';
import {
  cAddGroupMember, cGroupMembers, cRemoveGroupMember, cUpdateGroupMember,
} from '../../api';
import { useLanguage } from '../../i18n';
import UserAvatar from './UserAvatar';
import UserBadges from './UserBadges';
import { BLUE } from './brand';

/**
 * Group members — **the admins change the admins**: promote a member to admin,
 * remove the admin rights, transfer the ownership, change a member's group
 * **nickname** and remove somebody. Admins invite people by @username (and any
 * member can when the group allows member invites).
 */
export default function GroupMembers({ groupId, myRole = 'member', canInvite = false }) {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [username, setUsername] = useState('');
  const [role, setRole] = useState(myRole);
  const isAdmin = role === 'owner' || role === 'admin';

  const load = (term = '') => cGroupMembers(groupId, { q: term || undefined })
    .then((res) => { setItems(res.items || []); setRole(res.my_role || 'member'); })
    .catch(() => {});

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  const setMemberRole = async (member, value) => {
    try {
      const res = await cUpdateGroupMember(groupId, member.user_id, { role: value });
      setItems(res.members || items);
      toast.success(t('groupAdminChanged'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    }
  };

  const saveNickname = async (member) => {
    const value = window.prompt(t('groupNickname'), member.name || '');
    if (value === null) return;
    try {
      const res = await cUpdateGroupMember(groupId, member.user_id, { nickname: value });
      setItems(res.members || items);
      toast.success(t('saved'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    }
  };

  const remove = async (member) => {
    if (!window.confirm(t('groupRemoveMemberConfirm'))) return;
    try {
      const res = await cRemoveGroupMember(groupId, member.user_id);
      setItems(res.members || items);
      toast.success(t('deletedOk'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    }
  };

  const invite = async (e) => {
    e.preventDefault();
    const value = username.trim().replace(/^@/, '');
    if (!value) return;
    try {
      await cAddGroupMember(groupId, { username: value });
      toast.success(t('groupAdded'));
      setUsername('');
      load(q);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    }
  };

  const roleChip = (m) => {
    const label = m.role === 'owner' ? t('groupRoleOwner')
      : m.role === 'admin' ? t('groupRoleAdmin') : t('groupRoleMember');
    const cls = m.role === 'owner' ? 'bg-amber-50 text-amber-700'
      : m.role === 'admin' ? 'bg-blue-50 text-blue-700'
        : 'bg-gray-100 dark:bg-gray-800 text-gray-500';
    return <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${cls}`}>{label}</span>;
  };

  return (
    <div className="space-y-3">
      {canInvite && (
        <form onSubmit={invite} className="flex items-center gap-2">
          <FiUserPlus className="w-4 h-4 text-gray-400 shrink-0" />
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder={t('groupAddByUsername')}
            className="flex-1 min-w-0 rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-gray-900 px-3 py-2 text-sm"
          />
          <button type="submit" className="px-3.5 py-2 rounded-full text-xs font-bold text-white" style={{ background: BLUE }}>
            {t('groupAdd')}
          </button>
        </form>
      )}

      <div className="relative">
        <FiSearch className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); load(e.target.value); }}
          placeholder={t('search')}
          className="pl-9 pr-3 py-2 w-full rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-gray-900 text-sm"
        />
      </div>

      <ul className="divide-y divide-gray-100 dark:divide-gray-800">
        {items.map((m) => (
          <li key={m.id} className="flex items-center gap-3 py-2.5">
            <UserAvatar user={m.user} size={38} to={m.user ? `/u/${m.user.username}` : null} />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold truncate flex items-center gap-1.5">
                <span className="truncate">{m.name || m.user?.username}</span>
                <UserBadges user={m.user} />
                {m.me && <span className="text-[10px] text-gray-400">({t('you')})</span>}
              </p>
              <p className="text-[11px] text-gray-400 truncate">@{m.user?.username}</p>
            </div>
            {roleChip(m)}
            {isAdmin && !m.me && (
              <span className="flex items-center gap-1.5 flex-wrap justify-end">
                {m.role !== 'owner' && (
                  <button
                    type="button"
                    onClick={() => setMemberRole(m, m.role === 'admin' ? 'member' : 'admin')}
                    className="text-[11px] font-bold hover:underline"
                    style={{ color: BLUE }}
                  >
                    {m.role === 'admin' ? t('groupMakeMember') : t('groupMakeAdmin')}
                  </button>
                )}
                {role === 'owner' && m.role !== 'owner' && (
                  <button
                    type="button"
                    onClick={() => setMemberRole(m, 'owner')}
                    className="text-[11px] font-bold text-amber-600 hover:underline"
                  >
                    {t('groupTransferOwner')}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => saveNickname(m)}
                  className="text-[11px] font-bold text-gray-500 hover:underline"
                >
                  {t('groupNickname')}
                </button>
                {m.role !== 'owner' && (
                  <button
                    type="button"
                    onClick={() => remove(m)}
                    className="p-1 rounded text-red-500 hover:bg-red-50 dark:hover:bg-gray-800"
                    aria-label={t('remove')}
                  >
                    <FiX className="w-3.5 h-3.5" />
                  </button>
                )}
              </span>
            )}
          </li>
        ))}
      </ul>
      {items.length === 0 && <p className="text-xs text-gray-400">{t('groupEmpty')}</p>}
    </div>
  );
}